import { useIntl } from 'react-intl';

import { useAppConfigContext } from '~/contexts/AppConfig';
import { CheckboxGroup } from '~/eds/src/components/CheckboxGroup/CheckboxGroup';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { TicketSpecialOptionValueType } from '~/enums/TicketSpecialOptionValueType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { Nullable } from '~/types/Nullable';

import { CheckboxRadioCustomSettings } from '../types/fields';
import { WorkflowQuestionFormField } from './types';

export const CheckboxRadioWorkflowQuestion = ({
  question,
  onChange,
  type,
  ...commonProps
}: WorkflowQuestionFormField<Nullable<string[] | string>> & {
  type: WorkflowFieldType.Checkbox | WorkflowFieldType.Radio;
}) => {
  const { isPanelEnlarged } = useAppConfigContext();
  const intl = useIntl();
  const {
    field: { value, customSettings },
  } = question;
  const { options } = customSettings as CheckboxRadioCustomSettings;
  const { id, label, description, disabled, ...rest } = commonProps;

  const InputComponent =
    type === WorkflowFieldType.Checkbox ? CheckboxGroup : RadioGroup;

  return (
    <Layout direction="column" spacing={2}>
      <FormField
        {...rest}
        label={label}
        description={description}
        input={InputComponent}
        value={value.value ?? []}
        disabled={disabled}
        onChange={(val: Nullable<string[]>) => onChange(val, 'value')}
        inputProps={{
          options: options || [],
        }}
      />
      {value.value?.includes(TicketSpecialOptionValueType.Other) && (
        <FormField
          id={`${id}-other-option`}
          name={`${id}-other-option`}
          label={intl.formatMessage({
            id: 'CheckboxRadioWorkflowQuestion.otherOptionLabel',
            defaultMessage: 'Other option',
            description:
              'Label for other option field when Other is selected as an option in a checkbox or radio question on the intake form',
          })}
          input={TextInput}
          value={value.other_value}
          disabled={disabled}
          onChange={(val: Nullable<string>) => onChange(val, 'other_value')}
          inputProps={{
            width: isPanelEnlarged ? 'input.m.width' : '100%',
          }}
        />
      )}
    </Layout>
  );
};
